/**
 * Complementary products are configured per product, but a product usually has one variant per color. This element
 * makes the complementary cards follow the color selected on the product page: when a recommended product has a
 * variant of that color, the card shows the image, the link and the quick buy of that variant instead of the first one.
 */
if (!window.customElements.get('complementary-color-match')) {
  window.customElements.define(
    'complementary-color-match',
    class ComplementaryColorMatch extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section');
        this.colorVariants = JSON.parse(this.querySelector('script[data-color-variants]')?.textContent || '[]');

        this.onVariantChange = (event) => {
          // We ignore the variant pickers of the quick buy modals opened from the cards themselves
          if (this.contains(event.target)) {
            return;
          }

          this.matchColor(event.detail.variant?.options || []);
        };

        this.section?.addEventListener('variant:change', this.onVariantChange);
        this.matchColor(this.selectedOptions());
      }

      disconnectedCallback() {
        this.section?.removeEventListener('variant:change', this.onVariantChange);
      }

      /**
       * Reads the options of the variant currently selected on the product page
       */
      selectedOptions() {
        const variantData = this.section?.querySelector('variant-picker script[data-variant]');

        return JSON.parse(variantData?.textContent || '{}').options || [];
      }

      /**
       * For each card, we look for the option value that is also a color of the recommended product. The cards are
       * rendered in the same order as the color data, so we match them by index. As soon as one product exists in the
       * selected color, the products that do not exist in that color are hidden, so the suggestions stay one outfit.
       */
      matchColor(optionValues) {
        const values = optionValues.map((value) => value.toLowerCase().trim());

        this.querySelectorAll('.complementary-products__product-list').forEach((list) => {
          const cards = Array.from(list.children);
          const matchedColors = cards.map((card, index) => {
            const colors = this.colorVariants[index] || {};

            return Object.keys(colors).find((color) => values.includes(color.toLowerCase().trim()));
          });

          cards.forEach((card, index) => {
            const initialState = this.initialState(card);
            const colors = this.colorVariants[index] || {};

            this.applyState(card, colors[matchedColors[index]] || initialState);
          });

          const hiddenIndexes = matchedColors.some((color) => color)
            ? cards.map((card, index) => index).filter((index) => !matchedColors[index])
            : [];

          this.filterCards(list, hiddenIndexes);
        });
      }

      /**
       * Carousels have their own filtering, which also keeps the page dots in sync, while the stacked list is a plain
       * container whose cards we hide ourselves.
       */
      filterCards(list, hiddenIndexes) {
        if (typeof list.filter === 'function') {
          list.filter(hiddenIndexes);
        } else {
          Array.from(list.children).forEach((card, index) => {
            card.toggleAttribute('hidden', hiddenIndexes.includes(index));
          });
        }
      }

      /**
       * The state the card was rendered with, used when the selected color does not exist for this product
       */
      initialState(card) {
        if (!card.initialCardState) {
          const image = card.querySelector('img');

          card.initialCardState = {
            url: card.querySelector('a[href]')?.getAttribute('href'),
            image: image?.getAttribute('src'),
            srcset: image?.getAttribute('srcset'),
            handle: card.querySelector('quick-buy-modal')?.getAttribute('handle'),
          };
        }

        return card.initialCardState;
      }

      applyState(card, state) {
        if (state.url) {
          card.querySelectorAll('a[href]').forEach((link) => link.setAttribute('href', state.url));
        }

        const image = card.querySelector('img');

        if (image && state.image) {
          image.setAttribute('src', state.image);
          image.setAttribute('srcset', state.srcset || '');
        }

        const quickBuy = card.querySelector('quick-buy-modal');

        if (quickBuy && state.handle) {
          quickBuy.setAttribute('handle', state.handle);
        }
      }
    }
  );
}
